import { Boxes } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import type { CameraPose } from 'ai-tutor-player';
import type { Hotspot3DLabel } from '@/lib/schema.types';

/**
 * Shared `<model-viewer>` wrapper used everywhere a 3D model is shown or driven:
 * the canvas player, the asset library, the 3D properties panel, and the
 * keyframe editor. Centralises:
 *   - lazy-loading the `@google/model-viewer` custom element,
 *   - applying a controlled camera pose (playback / preview), with easing,
 *   - rendering 3D-anchored hotspot labels,
 *   - capturing the current camera pose (`getCameraOrbit`/`getCameraTarget`),
 *   - click-to-place: resolving a surface point + normal under the cursor.
 */

let loadPromise: Promise<void> | null = null;
export function loadModelViewer(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = import('@google/model-viewer')
    .then(() => undefined)
    .catch((err) => {
      loadPromise = null;
      throw err;
    });
  return loadPromise;
}

/** A point on the model surface, as model-viewer `data-position` / `data-normal` strings. */
export interface SurfaceHit {
  position: string;
  normal: string;
}

export interface ModelViewerHandle {
  /** Read the current camera pose (for "capture keyframe" / "set default pose"). */
  capturePose: () => CameraPose | null;
  /** Resolve the surface point + normal under a viewport pixel (for placing labels). */
  surfaceHitFromClient: (clientX: number, clientY: number) => SurfaceHit | null;
  /** Re-frame the model: reset the camera target to the model centre + a default orbit. */
  recenter: () => void;
  /** Escape hatch to the underlying custom element. */
  getElement: () => HTMLElement | null;
}

interface Props {
  src: string;
  poster?: string;
  alt?: string;
  exposure?: number;
  autoRotate?: boolean;
  cameraControls?: boolean;
  /** Controlled camera pose. When set, the viewer eases to it (playback/preview). */
  pose?: CameraPose | null;
  /** model-viewer `interpolation-decay` — lower = snappier easing. Default 100. */
  interpolationDecay?: number;
  /** 3D labels to render as hotspots (track the model as the camera moves). */
  labels?: Hotspot3DLabel[];
  className?: string;
  style?: React.CSSProperties;
  onReady?: () => void;
  onError?: (msg: string) => void;
  /** Fires with the surface hit when the model is clicked (label placement). */
  onModelClick?: (hit: SurfaceHit) => void;
}

export const ModelViewerCanvas = forwardRef<ModelViewerHandle, Props>(function ModelViewerCanvas(
  {
    src,
    poster,
    alt,
    exposure,
    autoRotate,
    cameraControls,
    pose,
    interpolationDecay = 100,
    labels,
    className,
    style,
    onReady,
    onError,
    onModelClick,
  },
  ref,
) {
  const elRef = useRef<HTMLElement | null>(null);
  const lastPoseRef = useRef<string>('');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(
    ref,
    (): ModelViewerHandle => ({
      capturePose: () => {
        const el = elRef.current as unknown as {
          getCameraOrbit?: () => { toString: () => string };
          getCameraTarget?: () => { toString: () => string };
          getFieldOfView?: () => number;
        } | null;
        if (!el || typeof el.getCameraOrbit !== 'function') return null;
        const orbit = el.getCameraOrbit?.();
        const target = el.getCameraTarget?.();
        const fov = el.getFieldOfView?.();
        if (!orbit) return null;
        return {
          cameraOrbit: orbit.toString(),
          cameraTarget: target ? target.toString() : undefined,
          fieldOfView: typeof fov === 'number' ? `${fov}deg` : undefined,
        };
      },
      surfaceHitFromClient: (clientX, clientY) => {
        const el = elRef.current as unknown as {
          positionAndNormalFromPoint?: (
            x: number,
            y: number,
          ) => { position: { toString: () => string }; normal: { toString: () => string } } | null;
        } | null;
        if (!el || typeof el.positionAndNormalFromPoint !== 'function') return null;
        // Raw client coords — model-viewer subtracts the element rect internally.
        const hit = el.positionAndNormalFromPoint?.(clientX, clientY);
        if (!hit) return null;
        return { position: hit.position.toString(), normal: hit.normal.toString() };
      },
      recenter: () => {
        const el = elRef.current;
        if (!el) return;
        el.setAttribute('camera-target', 'auto auto auto');
        el.setAttribute('camera-orbit', '0deg 75deg 105%');
        // Clear the dedupe so a later identical controlled pose still re-applies.
        lastPoseRef.current = '';
      },
      getElement: () => elRef.current,
    }),
    [],
  );

  // Lazy-load the custom element once.
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    loadModelViewer()
      .then(() => {
        if (cancelled) return;
        setReady(true);
        onReady?.();
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load 3D viewer.');
        onError?.('Could not load 3D viewer.');
      });
    return () => {
      cancelled = true;
    };
  }, [src, onReady, onError]);

  // Apply the controlled pose imperatively so model-viewer interpolates toward
  // it (setting the attributes triggers its built-in camera easing). Deduped by
  // VALUE, not object identity, so re-renders that pass an equal pose don't
  // re-apply it (which would fight the user's camera in edit mode).
  useEffect(() => {
    const el = elRef.current;
    if (!el || !ready || !pose) return;
    const key = `${pose.cameraOrbit}|${pose.cameraTarget ?? ''}|${pose.fieldOfView ?? ''}|${interpolationDecay}`;
    if (key === lastPoseRef.current) return;
    lastPoseRef.current = key;
    // Set the damper decay (move speed) IMMEDIATELY before the orbit goal so
    // model-viewer interpolates this move at the requested speed. Setting it
    // imperatively (not via a React prop) guarantees it reaches the element.
    el.setAttribute('interpolation-decay', String(interpolationDecay));
    if (pose.cameraOrbit) el.setAttribute('camera-orbit', pose.cameraOrbit);
    if (pose.cameraTarget) el.setAttribute('camera-target', pose.cameraTarget);
    if (pose.fieldOfView) el.setAttribute('field-of-view', pose.fieldOfView);
  }, [pose, ready, interpolationDecay]);

  // Surface picking via a NATIVE click listener (capture phase). React's
  // synthetic onClick on the custom element can be swallowed by model-viewer's
  // internal pointer handling; a native capture-phase listener fires reliably,
  // matching model-viewer's own "place hotspots interactively" example.
  useEffect(() => {
    const el = elRef.current as unknown as
      | (HTMLElement & {
          positionAndNormalFromPoint?: (
            x: number,
            y: number,
          ) =>
            | { position: { toString: () => string }; normal: { toString: () => string } }
            | null;
        })
      | null;
    if (!el || !ready || !onModelClick) return;
    const handler = (e: MouseEvent) => {
      if (typeof el.positionAndNormalFromPoint !== 'function') return;
      // model-viewer's getNDC() subtracts the element rect itself, so pass RAW
      // viewport client coords (subtracting the rect here would double-offset
      // and miss the mesh → null).
      const hit = el.positionAndNormalFromPoint(e.clientX, e.clientY);
      if (hit) onModelClick({ position: hit.position.toString(), normal: hit.normal.toString() });
    };
    el.addEventListener('click', handler, true);
    return () => el.removeEventListener('click', handler, true);
  }, [ready, onModelClick]);

  if (!src) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700 ${className ?? ''}`}
        style={style}
      >
        <Boxes size={24} />
        <span className="text-xs font-medium">No model</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-red-50 p-3 text-center text-xs text-red-600 ${className ?? ''}`}
        style={style}
      >
        <Boxes size={20} />
        {error}
      </div>
    );
  }

  if (!ready) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-bg-subtle text-fg-subtle ${className ?? ''}`}
        style={style}
      >
        <Boxes size={20} className="animate-pulse" />
        <span className="text-[11px] font-medium">Loading 3D viewer…</span>
      </div>
    );
  }

  return (
    // @ts-expect-error -- model-viewer is a custom element registered at runtime
    <model-viewer
      ref={elRef}
      src={src}
      poster={poster || undefined}
      alt={alt || '3D model'}
      camera-controls={cameraControls ? '' : undefined}
      auto-rotate={autoRotate ? '' : undefined}
      exposure={exposure ?? 1}
      shadow-intensity="1"
      interaction-prompt="none"
      class={className}
      // --min-hotspot-opacity: 0 → labels fully hide (not just dim) when their
      // anchor point faces away, so they stay glued to the 3D surface.
      style={{ ['--min-hotspot-opacity' as string]: 0, ...style } as React.CSSProperties}
    >
      <HotspotLabels labels={labels ?? []} />
      {/* @ts-expect-error -- closing custom element tag */}
    </model-viewer>
  );
});

// Label chip sits up-and-to-the-right of the click anchor, joined by a dashed
// connector. Screen-space px offsets (hotspots stay screen-aligned).
const LABEL_OX = 58;
const LABEL_OY = 46;

/**
 * Renders the 3D labels as model-viewer hotspots with a contact dot, a dashed
 * connector, and an offset chip. Keeps a label mounted through its exit
 * animation when it's removed (so labels animate both in and out as keyframes
 * activate/deactivate during playback).
 */
function HotspotLabels({ labels }: { labels: Hotspot3DLabel[] }) {
  const [rendered, setRendered] = useState<Array<{ label: Hotspot3DLabel; leaving: boolean }>>(() =>
    labels.map((label) => ({ label, leaving: false })),
  );
  const timers = useRef<Record<string, number>>({});

  useEffect(() => {
    const incoming = new Map(labels.map((l) => [l.id, l]));
    setRendered((prev) => {
      const next: Array<{ label: Hotspot3DLabel; leaving: boolean }> = [];
      for (const l of labels) {
        // Re-entering → cancel any pending exit removal.
        if (timers.current[l.id]) {
          window.clearTimeout(timers.current[l.id]);
          delete timers.current[l.id];
        }
        next.push({ label: l, leaving: false });
      }
      // Previously shown but gone now → keep, mark leaving for the exit anim.
      for (const r of prev) {
        if (!incoming.has(r.label.id)) next.push({ label: r.label, leaving: true });
      }
      return next;
    });
  }, [labels]);

  useEffect(() => {
    for (const r of rendered) {
      if (r.leaving && !timers.current[r.label.id]) {
        timers.current[r.label.id] = window.setTimeout(() => {
          delete timers.current[r.label.id];
          setRendered((prev) => prev.filter((p) => p.label.id !== r.label.id));
        }, 420);
      }
    }
  }, [rendered]);

  return (
    <>
      {rendered.map(({ label, leaving }) => {
        const anim = label.animation ?? 'fade';
        const phase = leaving ? `kf-exit-${anim}` : `kf-enter-${anim}`;
        const color = label.color;
        const left = label.side === 'left';
        return (
          <button
            key={label.id}
            slot={`hotspot-${label.id}`}
            data-position={label.position}
            data-normal={label.normal}
            className="kf-hotspot"
          >
            <span className={`kf-hotspot-inner ${phase}`}>
              <span
                className="kf-hotspot-dot"
                style={color ? { background: color } : undefined}
              />
              <svg
                className="kf-hotspot-connector"
                width={LABEL_OX}
                height={LABEL_OY}
                viewBox={`0 0 ${LABEL_OX} ${LABEL_OY}`}
                style={left ? { right: 0 } : { left: 0 }}
                aria-hidden="true"
              >
                <line
                  x1={left ? LABEL_OX - 1 : 1}
                  y1={LABEL_OY - 1}
                  x2={left ? 2 : LABEL_OX - 2}
                  y2="2"
                  stroke={color ?? '#0f172a'}
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              </svg>
              <span
                className="kf-hotspot-label"
                style={{
                  ...(left ? { right: LABEL_OX } : { left: LABEL_OX }),
                  bottom: LABEL_OY,
                  fontSize: label.fontSize ?? 13,
                  textAlign: left ? 'right' : 'left',
                }}
              >
                {label.text}
              </span>
            </span>
          </button>
        );
      })}
    </>
  );
}
